<template>
  <div class="hermeneus-icon"
       v-html="svgContent"
       :iconname="name"
       :style="{ width: `${width}px`, height: `${height}px` }"
       :class="{ [colorClass]: !preserveColors }"
       ref="iconRef">
  </div>
</template>

<script>
import {ref, onMounted, watch, computed} from 'vue';

const cache = new Map();

/**
 * Mit dieser Komponente können SVG-Icons dynamisch eingebunden werden.
 * Die Icons werden nur geladen, wenn sie im Viewport sichtbar sind.
 * Mit dem Prop 'name' wird der Name des Icons übergeben, das sich im Ordner 'public/img/svg' befindet.
 * (Beispiel: 'name="icon-name"' lädt das Icon 'icon-name.svg')
 * Als Farbe kann eine TailwindCSS-Klasse oder eine eigene Farbe übergeben werden.
 * Individuelle Farben können in der Datei 'custom-colors.css' definiert werden.
 * @file HermeneusIcon.vue
 * @description Komponente für das Einbinden von SVG-Icons
 * @module HermeneusIcon
 * @version 1.0.0
 */

export default {
  name: 'HermeneusIcon',
  props: {
    name: {
      type: String,
      required: true,
    },
    preserveColors: {
      type: Boolean,
      default: false,
    },
    width: {
      type: [String, Number],
      default: '15',
    },
    height: {
      type: [String, Number],
      default: '15',
    },
    color: {
      type: [String],
      default: 'current',
    },

    viewbox: {
      type: [Number, String],
      default: null,
    },
    strict: {
      type: Boolean,
      default: false,
    },
    cache: {
      type: Boolean,
      default: true,
    },
    fallbackIcon: {
      type: String,
      default: 'hi-404',
    }
  },
  computed: {
    /**
     * Gibt die Farbe des Icons zurück.
     * @returns {string}
     */
    colorClass () {
      if (this.color.startsWith('text-')) {
        return `current ${this.color}`;
      } else if(this.color.startsWith('#')) {
        return `text-[${this.color}]`;
      } else if (this.color.startsWith('hi-color-')) {
        return `${this.color}`;
      } else if (this.color === 'current') {
        return 'current';
      } else {
        return `text-${this.color}`;
      }



    }
  },
  setup (props, {refs}) {
    const svgContent = ref('');
    const iconRef = ref(null);
    /**
     * Gibt den Pfad zur SVG-Datei des Icons zurück.
     * @returns {string}
     */
    const iconSrc = computed(() => `/img/svg/${props.name}.svg`);
    const fallbackSrc = computed(() => `/img/svg/${props.fallbackIcon}.svg`);


    const loadSVG = async (src) => {
      if (!cache.has(src)) {
        try {
          const response = await fetch(src);
          // **
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const text = await response.text();
          cache.set(src, text);
        } catch (e) {
          console.warn(`Failed to load icon: ${src}`, e);
          cache.delete(src);
          // If this was the main icon load, try the fallback
          if (src === iconSrc.value) {
            return loadSVG(fallbackSrc.value);
          }
          return false;
        }
      }
      if (cache.has(src)) {
        svgContent.value = await cache.get(src);
      }
    };
    /**
     * Lazy loading mit der IntersectionObserver API
     * @type {IntersectionObserver}
     */

    const observer = new IntersectionObserver(
        async (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting) {
            await loadSVG(iconSrc.value);
            if (iconRef.value) {
              observer.unobserve(iconRef.value);
            }
          }
        },
        {
          rootMargin: '0px',
          threshold: 0.1,
        }
    );

    onMounted(() => {
      if (iconRef.value) {
        observer.observe(iconRef.value);
      }
    });

    watch(
        () => props.name,
        async () => {
          if (iconRef.value && cache.has(iconSrc.value)) {
            svgContent.value = await cache.get(iconSrc.value);
          }
        }
    );

    return {
      svgContent,
      iconRef,
    };
  },
};
</script>


<style>

@import './css/custom-colors.css';

.hermeneus-icon {
  display: inline-block;
}

.current path,
.current circle,
.current polygon,
.current *,
.current rect {
  fill: currentColor;
}

/* Override inline style fills only */
.current svg *[style*="fill"] {
  fill: currentColor !important;
}

</style>